import React from 'react';
import { motion } from 'framer-motion';
import { premiumWhiteCardStyle } from './allureCardStyles';

const font = "'Inria Serif', serif";

const actionButtonStyle = {
  borderRadius: 9999,
  padding: '8px 16px',
  border: '1px solid var(--card-border)',
  fontFamily: font,
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
};

export const GuestTipReminderCard = ({ prompt, dismissing, onDismiss, onOpen }) => {
  if (!prompt) {
    return null;
  }

  const visibleUntil = prompt.visible_until ? new Date(prompt.visible_until) : null;
  const remainingDays = visibleUntil ? Math.max(1, Math.ceil((visibleUntil.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;
  const reminderNote = remainingDays
    ? `Tip window ends in ${remainingDays} day${remainingDays === 1 ? '' : 's'}.${prompt.can_dismiss ? ' You can hide this reminder now.' : ''}`
    : (prompt.can_dismiss ? 'You can hide this reminder now.' : 'This reminder stays on your home page for up to 15 days.');

  return (
    <motion.div
      data-testid="guest-home-tip-reminder-card"
      initial={{ opacity: 0, y: 14 }}
      animate={{
        opacity: 1,
        y: 0,
        boxShadow: [
          '0 0 0 0 rgba(255, 255, 255, 0.0), var(--premium-shadow-main)',
          '0 0 0 8px rgba(255, 255, 255, 0.12), var(--premium-shadow-main)',
          '0 0 0 0 rgba(255, 255, 255, 0.0), var(--premium-shadow-main)',
        ],
      }}
      transition={{
        opacity: { duration: 0.35, ease: 'easeOut' },
        y: { duration: 0.35, ease: 'easeOut' },
        boxShadow: { duration: 2.4, repeat: Infinity, ease: 'easeInOut' },
      }}
      style={{
        width: '100%',
        maxWidth: 360,
        ...premiumWhiteCardStyle,
        padding: '16px 16px 14px',
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ display: 'grid', gap: 6 }}>
        <p data-testid="guest-home-tip-reminder-title" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--text)', lineHeight: 1.05, margin: 0, textAlign: 'left' }}>
          Show your appreciation to your host {prompt.host_name}
        </p>
        <p data-testid="guest-home-tip-reminder-message" style={{ fontFamily: font, fontSize: 17, color: 'var(--text-2)', lineHeight: 1.2, margin: 0, textAlign: 'left' }}>
          {prompt.message}
        </p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <button
          type="button"
          data-testid="guest-home-tip-reminder-open-button"
          onClick={onOpen}
          style={{
            ...actionButtonStyle,
            background: 'var(--solid-cta-bg)',
            color: 'var(--solid-cta-text)',
            boxShadow: '0 10px 20px rgba(17, 17, 17, 0.16)',
          }}
        >
          Open
        </button>

        {prompt.can_dismiss ? (
          <button
            type="button"
            data-testid="guest-home-tip-reminder-dismiss-button"
            onClick={onDismiss}
            disabled={dismissing}
            style={{
              ...actionButtonStyle,
              background: 'var(--surface-2)',
              color: 'var(--text-2)',
              opacity: dismissing ? 0.6 : 1,
            }}
          >
            Hide for now
          </button>
        ) : null}
      </div>

      <p data-testid="guest-home-tip-reminder-note" style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.15, margin: 0, textAlign: 'left' }}>
        {reminderNote}
      </p>
    </motion.div>
  );
};